import React, { useContext } from "react";
import PropTypes from "prop-types";
import { AccordionGroupContext } from "./AccordionGroup";
import { motion, AnimatePresence } from "framer-motion";

const normalizeText = (str) =>
  str
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

// Resalta texto plano
const highlightText = (text, query) => {
  if (!query || typeof text !== "string") return text;

  const normalizedText = normalizeText(text);
  const normalizedQuery = normalizeText(query);

  const result = [];
  let lastIndex = 0;

  let index = normalizedText.indexOf(normalizedQuery);

  while (index !== -1) {
    // Push el texto antes de la coincidencia
    if (index > lastIndex) {
      result.push(text.slice(lastIndex, index));
    }

    // Push el texto resaltado
    result.push(
      <mark key={index} className="rounded bg-yellow-300 p-0.5">
        {text.slice(index, index + query.length)}
      </mark>,
    );

    lastIndex = index + query.length;
    index = normalizedText.indexOf(normalizedQuery, lastIndex);
  }

  // Push el resto del texto
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
};

// Resalta dentro de contenido JSX
const highlightJSX = (node, query) => {
  if (!query || !node) return node;

  if (typeof node === "string") {
    return highlightText(node, query);
  }

  if (Array.isArray(node)) {
    return node.map((child, idx) => (
      <React.Fragment key={idx}>{highlightJSX(child, query)}</React.Fragment>
    ));
  }

  if (React.isValidElement(node)) {
    const children = highlightJSX(node.props.children, query);
    return React.cloneElement(node, { ...node.props, children });
  }

  return node;
};

const Accordion = ({ item, search = "" }) => {
  const { openAccordions, toggleAccordion } = useContext(AccordionGroupContext);
  const isOpen = openAccordions.includes(item.id);

  return (
    <div className="w-full">
      <div className="mb-2 rounded border">
        <div
          className="flex cursor-pointer items-center justify-between p-3"
          onClick={() => toggleAccordion(item.id)}
        >
          <span className="text-lg">{highlightText(item.title, search)}</span>
          <svg
            className={`h-6 w-6 transform transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden border-t px-3 py-2"
            >
              {item.content && (
                <div className="text-sm text-gray-700">
                  {highlightJSX(item.content, search)}
                </div>
              )}

              {item.children &&
                item.children.map((child) => (
                  <Accordion key={child.id} item={child} search={search} />
                ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Accordion;

Accordion.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.node,
    children: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  search: PropTypes.string,
};